/**
 * Trunnion AI - Template Preview Modal
 * Full detail view of a workflow template with description, tags,
 * classification, node/edge counts, and an instantiation action.
 */

import { useState } from 'react';
import { GitBranch, Link2, Tag, Users, Sparkles } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ClassificationTag } from '@/components/ui/ClassificationTag';
import type { WorkflowTemplate, TemplateCategory } from '@/types/template.types';
import type { ClassificationLevel } from '@/types';

interface TemplatePreviewModalProps {
  template: WorkflowTemplate | null;
  open: boolean;
  onClose: () => void;
  onInstantiate: (template: WorkflowTemplate, name: string) => void;
  isInstantiating?: boolean;
}

const CATEGORY_LABELS: Record<TemplateCategory, string> = {
  intelligence: 'Intelligence',
  logistics: 'Logistics',
  cyber: 'Cyber',
  admin: 'Admin',
  custom: 'Custom',
};

export function TemplatePreviewModal({
  template,
  open,
  onClose,
  onInstantiate,
  isInstantiating = false,
}: TemplatePreviewModalProps) {
  const [workflowName, setWorkflowName] = useState('');

  if (!template) return null;

  const nodeCount = template.graphDefinition?.nodes?.length ?? template.nodeCount ?? 0;
  const edgeCount = template.graphDefinition?.edges?.length ?? 0;
  const categoryLabel =
    CATEGORY_LABELS[template.category] ?? template.category ?? 'Custom';

  const handleInstantiate = () => {
    const name = workflowName.trim() || `${template.name} - New Workflow`;
    onInstantiate(template, name);
    setWorkflowName('');
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={template.name}
      description="Template details and instantiation"
      size="xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={isInstantiating}
            icon={<Sparkles className="h-4 w-4" />}
            onClick={handleInstantiate}
          >
            Instantiate
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Classification & Category */}
        <div className="flex items-center gap-3">
          <ClassificationTag
            level={
              (template.classificationLevel as ClassificationLevel) ??
              'UNCLASSIFIED'
            }
            size="md"
          />
          <Badge variant="info">{categoryLabel}</Badge>
          {template.isBuiltin && (
            <Badge variant="purple">Built-in</Badge>
          )}
        </div>

        {/* Description */}
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-trunnion-slate-400 mb-1.5">
            Description
          </h4>
          <p className="text-sm text-trunnion-slate-200 leading-relaxed">
            {template.description || 'No description provided.'}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-800/50 p-3 text-center">
            <div className="flex items-center justify-center gap-1.5 text-trunnion-slate-400 mb-1">
              <GitBranch className="h-3.5 w-3.5" />
              <span className="text-xs font-medium">Nodes</span>
            </div>
            <span className="text-lg font-semibold text-white">{nodeCount}</span>
          </div>
          <div className="rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-800/50 p-3 text-center">
            <div className="flex items-center justify-center gap-1.5 text-trunnion-slate-400 mb-1">
              <Link2 className="h-3.5 w-3.5" />
              <span className="text-xs font-medium">Edges</span>
            </div>
            <span className="text-lg font-semibold text-white">{edgeCount}</span>
          </div>
          <div className="rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-800/50 p-3 text-center">
            <div className="flex items-center justify-center gap-1.5 text-trunnion-slate-400 mb-1">
              <Users className="h-3.5 w-3.5" />
              <span className="text-xs font-medium">Uses</span>
            </div>
            <span className="text-lg font-semibold text-white">
              {template.useCount}
            </span>
          </div>
        </div>

        {/* Tags */}
        {template.tags && template.tags.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-trunnion-slate-400 mb-1.5 flex items-center gap-1.5">
              <Tag className="h-3 w-3" />
              Tags
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {template.tags.map((tag) => (
                <Badge key={tag} variant="default">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Workflow Name Input */}
        <div className="border-t border-trunnion-slate-700 pt-4">
          <Input
            label="New Workflow Name"
            placeholder={`${template.name} - New Workflow`}
            value={workflowName}
            onChange={(e) => setWorkflowName(e.target.value)}
            helperText="Leave blank to use the default name."
          />
        </div>
      </div>
    </Modal>
  );
}
